import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import AurDrawer from '../../common/aur-drawer/AurDrawer';
import AurButton from '../../common/aur-button/AurButton';
import {t} from 'i18next';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {AurSelect} from '../../common/aur-select/AurSelect';
import AurPopup from '../../common/aur-popup/AurPopup';
import AurTagSelectList from './AurTagSelectList';
import AddNewTag from './AddNewTag';
import {TaggingSectionInfo} from '../../../types/bulk-tagging/bulkTaggingTypes';
import {formateNumber} from '../../../utils/utils';
import {getNextDisplayOrder} from '../tag-utils';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import AurDataGridErrorOverlay from '../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {useGetEcaFields} from '../../../hooks/eca-tagging/useGetEcaFields';
import {useGetTagNames} from '../../../hooks/eca-tagging/useGetTagNames';
import {useUpdateTagNames} from '../../../hooks/eca-tagging/useUpdateTagNames';
import {AurLoadingButton} from '../../common/aur-loading-button/AurLoadingButton';
import {SSE_ENDPOINT_ECA, SSE_SERVICE_TYPE} from '../../../constants/constants';
import {useDispatch} from 'react-redux';
import {setTaggingStatus} from '../../../redux/slices/ecaDocListSlice';
import {ITag} from '../../pages/field-management/custom-field/DraggableTag';
import {IFieldModal} from '../../pages/field-management/custom-field/constant';
import {useStrUpdateTagNames} from '../../../hooks/str-tagging/useStrUpdateTagNames';
import {IFilterSet} from '../eca-doc-list.util';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';

export enum TaggingFLowType {
  ECA = 'ECA',
  STR = 'STR',
}

// mock data
const taggingSections: TaggingSectionInfo[] = [
  {
    title: 'ecaDocList.bulkTagging.tag',
    value: 'tag',
  },
  {
    title: 'ecaDocList.bulkTagging.untag',
    value: 'untag',
  },
];

interface BulkTaggingProps {
  showModal: boolean;
  handleModal: (flag: boolean, isSuccess?: boolean) => void;
  selectdDocumentIds: string[];
  allDocumentsSelected?: boolean;
  allDocumentsCount: number;
  searchId?: string;
  indexName?: string;
  flowType: TaggingFLowType;
  selectedDocHitParam?: string;
  fieldTemplateId?: string;
  strReportId?: string;
  filterset?: IFilterSet;
}

const BulkTaggingModal = forwardRef<HTMLButtonElement, BulkTaggingProps>(
  (
    {
      showModal,
      handleModal,
      selectdDocumentIds,
      allDocumentsSelected = false,
      allDocumentsCount,
      searchId,
      indexName = '',
      flowType,
      selectedDocHitParam = '',
      fieldTemplateId = '',
      strReportId = '',
      filterset,
    },
    ref
  ) => {
    const [selectedTagging, setSelectedTagging] = useState<string>('tag');
    const [requestId, setRequestId] = useState<string | null>();
    const [selectedTags, setSelectedTags] = useState<ITag[]>([]);
    const [newTags, setNewTags] = useState<ITag[]>([]);
    const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
    const [selectedTagField, setSelectedTagField] = useState<IFieldModal>();
    const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
    const [isError, setIsError] = useState(false);

    const dispatch = useDispatch();
    const closePopupRef = useRef<HTMLButtonElement>(null);
    const {trackGAEvent} = useAnalytics();

    const {
      getFields,
      fields,
      isFailedToLoadContent: isFailedToLoadFieldContent,
    } = useGetEcaFields();

    const {
      getAllTagNames,
      isFailedToLoadContent: isFailedToLoadTagNameContent,
      isLoading,
      resetErrorFlag,
    } = useGetTagNames();

    const {updateTagName, isLoading: isLoadingOnApply} = useUpdateTagNames();
    const {updateStrTagName, isLoading: isStrLoadingOnApply} =
      useStrUpdateTagNames();

    useEffect(() => {
      getFields();
    }, []);

    useEffect(() => {
      if (requestId) {
        const SSE_URL = `${SSE_ENDPOINT_ECA}/sse/${requestId}/${SSE_SERVICE_TYPE.TAG_SERVICE}`;
        const eventSource = new EventSource(SSE_URL);

        eventSource.onmessage = (event) => {
          const responseData = JSON.parse(event.data);
          dispatch(setTaggingStatus(responseData.status));
          eventSource.close();
        };

        eventSource.onerror = (error) => {
          console.error('SSE Connection Error:', error);
          dispatch(setTaggingStatus('Failed'));
          eventSource.close();
        };
        return () => {
          console.log('Closing SSE connection');
          dispatch(setTaggingStatus(null));
          eventSource.close();
        };
      }
    }, [requestId]);

    const getCheckedRadioIcon = () => '/assests/images/radio-checked.svg';
    const getUncheckedRadioIcon = () => '/assests/images/radio-unchecked.svg';

    const radioIcon = (value: string, selected: string): string => {
      return value === selected
        ? getCheckedRadioIcon()
        : getUncheckedRadioIcon();
    };

    const handleTaggingSelection = (value: string) => {
      setSelectedTagging(value);
    };

    const tagFieldOptions = useMemo(() => {
      return fields?.map((field) => ({
        key: field.fieldId.toString(),
        value: field.fieldId.toString(),
        label: field.fieldLabel,
      }));
    }, [fields]);

    const handleClear = () => {
      setIsError(false);
      setSelectedTagging('tag');
      setSelectedFieldId(null);
      setSelectedTagField(undefined);
      setNewTags([]);
      setSelectedTags([]);
      resetErrorFlag();
    };

    const isStateChanged = () => {
      return selectedTagging !== 'tag' || !!selectedTagField;
    };

    const isApplyEnabled = useCallback(() => {
      return !!selectedTagField && selectedTags?.length > 0;
    }, [selectedTagField, selectedTags?.length]);

    const handleClose = () => {
      if (isStateChanged()) {
        setConfirmationModal(true);
        setTimeout(() => {
          closePopupRef.current?.focus();
        }, 100);
        trackGAEvent({
          eventName: GA_EVENT_NAMES.CLICK,
          click: {
            name: t('gaTracking.global.close'),
            type: GA_CLICK_TYPES.BUTTON,
            region: GA_REGIONS.DRAWER,
            action: t('gaTracking.str.bulkTaggingClosed'),
          },
          subPage: {
            subpage_name: t('gaTracking.str.bulkTaggingTags'),
            subpage_type: GA_SUBPAGE_TYPE.DRAWER,
          },
        });
        trackGAEvent({
          eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
          subPage: {
            subpage_name: t('gaTracking.str.closeTheBulkTagging'),
            subpage_type: GA_SUBPAGE_TYPE.MODAL,
          },
        });
      } else {
        handleModal(false);
        handleClear();
      }
    };

    const handleApply = async () => {
      const tagIds = selectedTags.map((tag) => tag.tagValueId);
      if (flowType === TaggingFLowType.ECA) {
        const requestBody = {
          allDocuments: allDocumentsSelected,
          documentIds: allDocumentsSelected ? [] : selectdDocumentIds,
          searchId: allDocumentsSelected && searchId ? Number(searchId) : null,
          tagFieldId: selectedTagField?.fieldId,
          tagIds: tagIds,
          filterset:
            allDocumentsSelected && filterset?.items.length ? filterset : null,
        };
        const onSuccess = (requestId?: string) => {
          handleModal(false);
          handleClear();
          setRequestId(requestId);
        };
        await updateTagName(selectedTagging, indexName, requestBody, onSuccess);
      } else if (flowType === TaggingFLowType.STR) {
        const requestBody = {
          terms: selectdDocumentIds,
          docHitsParams: {
            [selectedDocHitParam]: true,
          },
          tagValueIds: tagIds,
          tagFieldId: selectedTagField?.fieldId,
        };
        const onSuccess = (requestId?: string) => {
          handleModal(false, true);
          handleClear();
          setRequestId(requestId);
        };
        await updateStrTagName(
          selectedTagging,
          strReportId,
          fieldTemplateId,
          requestBody,
          onSuccess
        );
      }

      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.global.apply'),
          type: GA_CLICK_TYPES.BUTTON,
          region: GA_REGIONS.DRAWER,
          action: t('gaTracking.str.applyTaggingAction'),
          details: t('gaTracking.str.bulkTaggingDetails', {
            actionName: selectedTagging === 'tag' ? 'tag' : 'untag',
          }),
        },
        subPage: {
          subpage_name: t('gaTracking.str.bulkTaggingTags'),
          subpage_type: GA_SUBPAGE_TYPE.DRAWER,
        },
      });
    };

    const confirmationModalCloseHandler = (isCancelled: boolean) => {
      setConfirmationModal(false);
      if (isCancelled) {
        trackGAEvent({
          eventName: GA_EVENT_NAMES.CLICK,
          click: {
            name: t('gaTracking.str.cancel'),
            type: GA_CLICK_TYPES.BUTTON,
            region: GA_REGIONS.MODAL,
            action: t('gaTracking.str.bulkTaggingClosed'),
          },
          subPage: {
            subpage_name: t('gaTracking.str.closeTheBulkTagging'),
            subpage_type: GA_SUBPAGE_TYPE.MODAL,
          },
        });
      } else {
        trackGAEvent({
          eventName: GA_EVENT_NAMES.CLICK,
          click: {
            name: t('gaTracking.str.bulkTaggingSure'),
            type: GA_CLICK_TYPES.BUTTON,
            region: GA_REGIONS.MODAL,
            action: t('gaTracking.str.continueBulkTagging'),
          },
          subPage: {
            subpage_name: t('gaTracking.str.closeTheBulkTagging'),
            subpage_type: GA_SUBPAGE_TYPE.MODAL,
          },
        });
      }
    };

    const confirmationModalOkHandler = () => {
      confirmationModalCloseHandler(false);
      handleModal(false);
      handleClear();
    };

    const renderSubHeader = () => {
      return (
        <Typography className={`typo-text body4_size selected-count-text`}>
          {t('ecaDocList.bulkTagging.tagsSelectedCount', {
            selectedCount: selectedTags.length,
          })}
        </Typography>
      );
    };

    const updateSelectedTags = useCallback((tags: ITag[]) => {
      setSelectedTags(tags);
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.ecaDocList.gaSelectTag'),
          type: GA_CLICK_TYPES.CHECKBOX,
          region: GA_REGIONS.DRAWER,
          action: t('gaTracking.ecaDocList.gaStep2'),
        },
        subPage: {
          subpage_name: t('gaTracking.ecaDocList.bulkTaggingtitle'),
          subpage_type: GA_SUBPAGE_TYPE.DRAWER,
        },
      });
    }, []);

    const handleAddTag = useCallback((newTag: ITag) => {
      const updatePreviousTagsList = (prev: ITag[]) => [newTag, ...prev];
      setNewTags(updatePreviousTagsList);
      setSelectedTags(updatePreviousTagsList);
    }, []);

    const fetchAllTagName = async (selectedValue: string) => {
      const result = await getAllTagNames(selectedValue);
      setSelectedTagField(result);
      setSelectedTags([]);
      setNewTags([]);
    };
    const handleTagFieldChange = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any

      (event: any) => {
        const selectedValue = event.target.value;
        setSelectedFieldId(selectedValue);

        // fire-and-forget async call
        void (async () => {
          await fetchAllTagName(selectedValue);
        })();
      },
      [fields]
    );

    const allTags = useMemo(() => {
      return selectedTagField ? selectedTagField.tagValues : [];
    }, [selectedTagField]);

    const nextDisplayOrder = useMemo(() => {
      return getNextDisplayOrder([...allTags, ...newTags]);
    }, [allTags, newTags]);

    const failedToLoadContent = () => (
      <div className="content-loading-error">
        <AurDataGridErrorOverlay
          onRefresh={
            isFailedToLoadFieldContent
              ? getFields
              : () => fetchAllTagName(selectedFieldId ?? '')
          }
          enableParentWrapper={false}
        />
      </div>
    );

    const renderTaggingContent = () => {
      if (isLoading) {
        return (
          <Box>
            <AurLoadingIndicator show={true} />
          </Box>
        );
      }

      if (isFailedToLoadTagNameContent) {
        return failedToLoadContent();
      }

      return (
        <>
          {selectedTagField && (
            <Box className="tag-list-section" aria-hidden>
              <AurTagSelectList
                onSelectionChange={updateSelectedTags}
                selectedTags={selectedTags}
                renderSubHeader={renderSubHeader}
                items={[...newTags, ...allTags]}
                selectAllLabel={t('common.selectAll')}
                tagAction={selectedTagging}
                isError={isError}
                height={360}
              />
            </Box>
          )}

          {selectedTagging === 'tag' && selectedTagField && (
            <Box className="add-new-tag-section">
              <AddNewTag
                onAddClick={handleAddTag}
                allTags={[...allTags, ...newTags]}
                isError={isError}
                setError={setIsError}
                tagFieldId={selectedTagField.fieldId.toString()}
                displayOrder={nextDisplayOrder}
              />
            </Box>
          )}
        </>
      );
    };

    const contentBody = (
      <Box className="bulk-tagging-modal">
        <Box className="close-btn-container">
          <AurButton
            ref={ref}
            label={t('buttons.close')}
            type="exit"
            size="Medium"
            title={t('buttons.close')}
            onClick={() => handleClose()}
          />
        </Box>
        <Box className="panel-header">
          <Box className="header-content" aria-hidden>
            <Typography
              className="typo-text headline1_size title"
              data-testid="bulk-tagging-title"
            >
              {t('ecaDocList.bulkTagging.title')}
            </Typography>
            <Box>
              <AurButton
                label={t('buttons.clear')}
                disabled={!isStateChanged()}
                type="exit"
                size="Medium"
                title={t('buttons.clear')}
                onClick={() => handleClear()}
              />

              <AurLoadingButton
                label={t('buttons.apply')}
                loading={isLoadingOnApply || isStrLoadingOnApply}
                onClick={() => handleApply()}
                size="Medium"
                minWidth={151}
                buttonType="submit"
                classNames={`typo-text custom-button ${(isLoadingOnApply || isStrLoadingOnApply) && 'apply-button-loading'}`}
                disabled={
                  isLoadingOnApply || !isApplyEnabled() || isStrLoadingOnApply
                }
                dataTestId="apply-tagging"
              />
            </Box>
          </Box>
          {/* HIDDEN FOR STR AS PER ARHD-17576 */}
          {flowType != TaggingFLowType.STR && (
            <Box className="subheader-content">
              <Typography className="typo-text subhead3_size title">
                {t('ecaDocList.bulkTagging.totalSelectedDocuments')}
                <span className="typo-text body1_size documents">
                  {formateNumber(allDocumentsCount)}
                </span>
              </Typography>
            </Box>
          )}
        </Box>
        {isFailedToLoadFieldContent ? (
          failedToLoadContent()
        ) : (
          <Box className="action-field-container">
            <Box className="text">
              <Typography className="typo-text subhead4_size title">
                {t('ecaDocList.bulkTagging.selectActionAndField')}
              </Typography>
            </Box>
            <Box className="action" aria-hidden>
              <Box>
                <RadioGroup
                  row
                  aria-label={t('aria.selectAction')}
                  value={selectedTagging}
                  onChange={(event) =>
                    handleTaggingSelection(event.target.value)
                  }
                >
                  {taggingSections.map((item) => (
                    <FormControlLabel
                      className="radio-label"
                      key={`radio-${item.value}`}
                      value={item.value}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setSelectedTagging(item.value);
                        }
                      }}
                      control={
                        <Radio
                          checkedIcon={
                            <img
                              src={radioIcon(item.value, selectedTagging)}
                              alt={`${item.title} ${item.value === selectedTagging ? 'selected' : 'unselected'}`}
                            />
                          }
                        />
                      }
                      label={
                        <Typography className="typo-text body2_size">
                          {t(item.title)}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              </Box>
              <AurSelect
                dataTestId="aur-select-tag-field-dropdown"
                label={t('ecaDocList.bulkTagging.selectTagField')}
                labelId="select-tags"
                optionList={tagFieldOptions}
                field={{
                  value: selectedFieldId || '',
                  onChange: handleTagFieldChange,
                }}
                fieldState={{}}
                style={{width: '625px'}}
              />
            </Box>
          </Box>
        )}

        {renderTaggingContent()}

        {confirmationModal && (
          <AurPopup
            className="aur-unsaved-changes-popup"
            ref={closePopupRef}
            open={confirmationModal}
            onOk={confirmationModalOkHandler}
            onCancel={() => confirmationModalCloseHandler(true)}
            popupHeading={t(
              'ecaDocList.bulkTagging.confirmationModalPopup.popupHeading'
            )}
            popupTitle={t(
              'ecaDocList.bulkTagging.confirmationModalPopup.popupTitle'
            )}
            showPopupHeading={true}
            showPopupTitle={true}
            popupContent={t(
              'ecaDocList.bulkTagging.confirmationModalPopup.popupContent'
            )}
            showCloseIcon={true}
            labelActionCancel={t(
              'ecaDocList.bulkTagging.confirmationModalPopup.buttonCancel'
            )}
            labelActionOk={t(
              'ecaDocList.bulkTagging.confirmationModalPopup.buttonYes'
            )}
            cancelButtonTitle=""
          />
        )}
      </Box>
    );

    return (
      <AurDrawer
        open={showModal}
        onClose={() => handleClose()}
        anchor="right"
        drawerContent={contentBody}
      />
    );
  }
);

export default BulkTaggingModal;
